import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetPatientIdentityByIdResponse } from "src/api/Tmd.dtos";
import PatientIdentityContext from "src/contexts/data/PatientIdentityContext";
import usePatientIdentityById from "src/hooks/data/load/usePatientIdentityById";

/**
 * Configures the patient identity request and the descendants receiving its state.
 */
interface IPatientProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<
        GetPatientIdentityByIdResponse,
        GetPatientIdentityByIdResponse
    >;
}

/**
 * Publishes the patient identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const PatientIdentityProvider: FC<IPatientProviderProps> = ({ children, id, configuration }) => {
    const context = usePatientIdentityById(id, configuration);

    return (
        <PatientIdentityContext.Provider value={context}>
            {children}
        </PatientIdentityContext.Provider>
    );
};

/**
 * Loads patient identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RoutePatientIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { patientId } = useParams<{ patientId: string }>();
    const id = patientId ? parseInt(patientId, 10) : undefined;

    return <PatientIdentityProvider id={id}>{children}</PatientIdentityProvider>;
};

export default PatientIdentityProvider;
